/**
 * @file schemas.ts
 * @brief Zod schemas + DTO types for platform-wide (super-admin) MeetingIntel.
 *
 * @details
 * VERBLIZR staff see metadata only, never meeting content (B2/W4, DES
 * 22-10-2026). These schemas list every field the Super Admin pages may hold:
 * owner, company, date, duration, status, counts and money. Zod strips any
 * other key, so a transcript, intelligence, to-dos, participants or a signed
 * URL never reach the page even from a backend that still sends them (the
 * live one, until BACKEND 1fd613a is deployed). Do not add `.passthrough()`.
 *
 * Tolerant rows (W33, 30-10-2026): every field but `id` and `status` falls
 * back to null when its value is not the expected type, so one odd row can
 * never fail the whole list again. `asr` is gone: the backend sends
 * `metrics->'asr'` (the ASR chain record, a JSON object since 14-10-2026)
 * and the old `asr: optText` threw on every meeting processed since, which
 * blanked every Super Admin meetings view ("Showing 0 of 9"). No page read it.
 *
 * @dependencies
 * - zod
 * - features/company-meetings/meetingStatus (status enum)
 *
 * @references
 * - BACKEND/src/features/boardroom/admin.routes.js (payload source)
 * - scratch/claude-code-report.md "## B2" (the contract)
 * - features/admin-meetings/api.ts (consumer)
 *
 * @author DES
 * @date 08-07-2026
 * @updated 22-10-2026 — metadata only (W4)
 * @updated 30-10-2026 — tolerant rows, no `asr` (W33)
 */

import { z } from "zod";
import { MeetingStatusSchema } from "../company-meetings/meetingStatus.ts";

const numericString = z.union([
  z.number(),
  z
    .string()
    .transform((v) => (v === "" ? 0 : Number(v)))
    .pipe(z.number()),
]);

// A value of the wrong type reads as null instead of failing the row.
const optText = z.string().nullable().optional().catch(null);
const optNumber = numericString.nullable().optional().catch(null);
const optBool = z.boolean().nullable().optional().catch(null);

// ----------------------------------------------------------------------
// List row + detail: metadata only (B2 contract). Unknown keys are stripped.
// Types follow STAFF_MEETING_COLUMNS (BACKEND admin.routes.js). `status`
// reads an unknown value as "error", like the company schema.
// ----------------------------------------------------------------------
export const AdminMeetingRowSchema = z.object({
  id: z.string(),
  owner_id: optText,
  title: optText,
  started_at: optText,
  total_duration_seconds: optNumber,
  recording_size_bytes: optNumber,
  status: MeetingStatusSchema.catch("error"),
  tier: z.enum(["notes", "full"]).nullable().optional().catch(null),
  source: optText,
  target_languages: z.array(z.string()).nullable().optional().catch(null),
  failure_reason: optText,
  failed_at: optText,
  heartbeat_at: optText,
  created_at: optText,
  updated_at: optText,
  currency: optText,
  charge_cents: optNumber,
  total_cost_cents: optNumber,
  is_invoiced: optBool,
  has_recording: optBool,
  has_mom_pdf: optBool,
  participant_count: optNumber,
  speaker_count: optNumber,
  decision_count: optNumber,
  todo_count: optNumber,
  bookmark_count: optNumber,
  owner_first_name: optText,
  owner_last_name: optText,
  owner_email: optText,
  owner_avatar_url: optText,
  company_id: optText,
  company_name: optText,
});

export type AdminMeetingRow = z.infer<typeof AdminMeetingRowSchema>;

export const AdminMeetingDetailSchema = AdminMeetingRowSchema.extend({
  transcript_segment_count: optNumber,
  is_owner: optBool,
  content_hidden: optBool,
});

export type AdminMeetingDetail = z.infer<typeof AdminMeetingDetailSchema>;

// ----------------------------------------------------------------------
// Response envelopes
// ----------------------------------------------------------------------
export const AdminMeetingsListResponseSchema = z.object({
  success: z.boolean().optional(),
  meetings: z.array(AdminMeetingRowSchema).default([]),
  page: numericString.optional(),
  pageSize: numericString.optional(),
  total: numericString.optional(),
  hasMore: z.boolean().optional(),
});

export type AdminMeetingsListResponse = z.infer<
  typeof AdminMeetingsListResponseSchema
>;

export const AdminMeetingDetailResponseSchema = z.object({
  success: z.boolean().optional(),
  meeting: AdminMeetingDetailSchema,
});

/** GET /sessions/:id/cogs — vendor cost next to the customer bill. */
export const AdminMeetingCogsSchema = z.object({
  measured: z.boolean().optional(),
  cogs: z
    .object({ cogs_total_cents: numericString.nullable().optional() })
    .nullable()
    .optional(),
  margin_pct: numericString.nullable().optional(),
});

export type AdminMeetingCogs = z.infer<typeof AdminMeetingCogsSchema>;

// ----------------------------------------------------------------------
// List query params — adds owner_id + company_id (admin can slice by either);
// no employee_id (that's the company-scope filter).
// ----------------------------------------------------------------------
export type AdminMeetingsListParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  from?: string;
  to?: string;
  owner_id?: string;
  company_id?: string;
};

export function meetingOwnerLabel(row: AdminMeetingRow): string {
  const ownerName = [row.owner_first_name, row.owner_last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (ownerName) return ownerName;
  if (row.owner_email) return row.owner_email;
  return "—";
}

export function meetingOwnerKey(row: AdminMeetingRow): string {
  if (row.owner_id?.trim()) return row.owner_id.trim();
  if (row.owner_email?.trim()) return row.owner_email.trim().toLowerCase();
  return meetingOwnerLabel(row).toLowerCase();
}

/** Recorded minutes, one decimal ("12.5 min"). Not a billed figure. */
export function formatMeetingMinutes(row: { total_duration_seconds?: number | null }): string {
  const min = Number(row.total_duration_seconds ?? 0) / 60;
  return `${Number.isFinite(min) ? min.toFixed(1) : "0.0"} min`;
}
